import { Point } from '../point';

export type IntersectionType = 'Intersection' | 'Coincident' | 'Parallel';

const createVector = (from: Point, to: Point): Point =>
  new Point(to.x - from.x, to.y - from.y);

const hasIntersection = (status?: IntersectionType): boolean =>
  status === 'Intersection' || status === 'Coincident';

export class Intersection {
  points: Point[] = [];
  status?: IntersectionType;

  constructor(status?: IntersectionType) {
    this.status = status;
  }

  private includes(point: Point): boolean {
    return this.points.some((candidate) => candidate.eq(point));
  }

  private append(...points: Point[]): Intersection {
    this.points = this.points.concat(points.filter((point) => !this.includes(point)));
    return this;
  }

  static isPointContained(point: Point, start: Point, end: Point, infinite = false): boolean {
    if (start.eq(end)) {
      return point.eq(start);
    }

    if (start.x === end.x) {
      return (
        point.x === start.x &&
        (infinite || (point.y >= Math.min(start.y, end.y) && point.y <= Math.max(start.y, end.y)))
      );
    }

    if (start.y === end.y) {
      return (
        point.y === start.y &&
        (infinite || (point.x >= Math.min(start.x, end.x) && point.x <= Math.max(start.x, end.x)))
      );
    }

    const ab = createVector(start, end);
    const ap = createVector(start, point);
    const scale = ap.divide(ab);

    return infinite
      ? Math.abs(scale.x) === Math.abs(scale.y)
      : scale.x === scale.y && scale.x >= 0 && scale.x <= 1;
  }

  static isPointInPolygon(point: Point, polygon: Point[]): boolean {
    const rayEnd = new Point(point).setX(Math.min(point.x - 1, ...polygon.map((candidate) => candidate.x)));
    let hits = 0;

    for (let index = 0; index < polygon.length; index += 1) {
      const intersection = this.intersectSegmentSegment(
        polygon[index],
        polygon[(index + 1) % polygon.length],
        point,
        rayEnd
      );

      if (intersection.includes(point)) {
        return true;
      }

      hits += Number(intersection.status === 'Intersection');
    }

    return hits % 2 === 1;
  }

  static intersectLineLine(
    a1: Point,
    a2: Point,
    b1: Point,
    b2: Point,
    aInfinite = true,
    bInfinite = true
  ): Intersection {
    const a2xa1x = a2.x - a1.x;
    const a2ya1y = a2.y - a1.y;
    const b2xb1x = b2.x - b1.x;
    const b2yb1y = b2.y - b1.y;
    const a1xb1x = a1.x - b1.x;
    const a1yb1y = a1.y - b1.y;
    const uaT = b2xb1x * a1yb1y - b2yb1y * a1xb1x;
    const ubT = a2xa1x * a1yb1y - a2ya1y * a1xb1x;
    const uB = b2yb1y * a2xa1x - b2xb1x * a2ya1y;

    if (uB !== 0) {
      const ua = uaT / uB;
      const ub = ubT / uB;

      if ((aInfinite || (ua >= 0 && ua <= 1)) && (bInfinite || (ub >= 0 && ub <= 1))) {
        return new Intersection('Intersection').append(
          new Point(a1.x + ua * a2xa1x, a1.y + ua * a2ya1y)
        );
      }

      return new Intersection();
    }

    if (uaT === 0 || ubT === 0) {
      const segmentsCoincide =
        aInfinite ||
        bInfinite ||
        Intersection.isPointContained(a1, b1, b2) ||
        Intersection.isPointContained(a2, b1, b2) ||
        Intersection.isPointContained(b1, a1, a2) ||
        Intersection.isPointContained(b2, a1, a2);

      return new Intersection(segmentsCoincide ? 'Coincident' : undefined);
    }

    return new Intersection('Parallel');
  }

  static intersectSegmentLine(start: Point, end: Point, lineStart: Point, lineEnd: Point): Intersection {
    return Intersection.intersectLineLine(start, end, lineStart, lineEnd, false, true);
  }

  static intersectSegmentSegment(a1: Point, a2: Point, b1: Point, b2: Point): Intersection {
    return Intersection.intersectLineLine(a1, a2, b1, b2, false, false);
  }

  static intersectLinePolygon(start: Point, end: Point, polygon: Point[], infinite = true): Intersection {
    const result = new Intersection();

    for (let i = 0; i < polygon.length; i += 1) {
      const edgeStart = polygon[i];
      const edgeEnd = polygon[(i + 1) % polygon.length];
      const intersection = Intersection.intersectLineLine(start, end, edgeStart, edgeEnd, infinite, false);

      if (intersection.status === 'Coincident') {
        return intersection;
      }

      result.append(...intersection.points);
    }

    if (result.points.length > 0) {
      result.status = 'Intersection';
    }

    return result;
  }

  static intersectSegmentPolygon(start: Point, end: Point, polygon: Point[]): Intersection {
    return Intersection.intersectLinePolygon(start, end, polygon, false);
  }

  static intersectPolygonPolygon(first: Point[], second: Point[]): Intersection {
    const result = new Intersection();
    const coincidences: Intersection[] = [];

    for (let i = 0; i < first.length; i += 1) {
      const edgeStart = first[i];
      const edgeEnd = first[(i + 1) % first.length];
      const intersection = Intersection.intersectSegmentPolygon(edgeStart, edgeEnd, second);

      if (intersection.status === 'Coincident') {
        coincidences.push(intersection);
        result.append(edgeStart, edgeEnd);
      } else {
        result.append(...intersection.points);
      }
    }

    if (coincidences.length > 0 && coincidences.length === first.length) {
      return new Intersection('Coincident');
    }

    if (result.points.length > 0) {
      result.status = 'Intersection';
    }

    return result;
  }

  static intersectPolygonRectangle(polygon: Point[], first: Point, second: Point): Intersection {
    const min = first.min(second);
    const max = first.max(second);
    const topRight = new Point(max.x, min.y);
    const bottomLeft = new Point(min.x, max.y);

    return Intersection.intersectPolygonPolygon(polygon, [min, topRight, max, bottomLeft]);
  }
}

export const hasIntersectionPoints = (intersection: Intersection): boolean =>
  hasIntersection(intersection.status) && intersection.points.length > 0;
